import * as Sentry from '@sentry/react'
import { useRouter } from '@tanstack/react-router'
import { Button, Input } from '@repo/ui'
import { useState } from 'react'
import { saveConnectorDescription } from '@/api/content/connector'

export function ConnectorDescription({
  connectorDescription,
  editMode,
  connectorId,
  onClose,
  onCancelEdit,
  onSaved
}: {
  connectorDescription: string
  editMode: boolean
  connectorId: number
  onClose: () => void
  onCancelEdit: () => void
  onSaved?: () => void | Promise<void>
}) {
  const router = useRouter()
  const [description, setDescription] = useState(connectorDescription)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSave() {
    try {
      setError(null)
      setSubmitting(true)
      await saveConnectorDescription(connectorId, description)
      if (onSaved !== undefined) await onSaved()
      else await router.invalidate()
      onClose()
    } catch (err) {
      Sentry.captureException(err, { extra: { connectorId } })
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSubmitting(false)
    }
  }

  function handleCancel() {
    setDescription(connectorDescription)
    setError(null)
    onCancelEdit()
  }

  if (!editMode) {
    return <div className="rounded-md border bg-muted/30 p-3">{connectorDescription}</div>
  }

  return (
    <>
      <div>
        <Input
          placeholder="Connector Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSave()
          }}
        />
      </div>
      {error && (
        <p role="alert" className="mt-1 text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={submitting} onClick={handleSave}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </>
  )
}
